import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { House, CaretRight } from '@phosphor-icons/react';

// Route configuration for breadcrumb labels and hierarchy
const routeConfig = {
  // Dashboards
  '/contractor-dashboard': { label: 'Dashboard', parent: null },
  '/worker-dashboard': { label: 'Dashboard', parent: null },
  
  // Control Panels
  '/contractor-control-panel': { label: 'Settings', parent: '/contractor-dashboard' },
  '/worker-control-panel': { label: 'Settings', parent: '/worker-dashboard' },
  
  // Jobs
  '/my-jobs': { label: 'My Jobs', parent: '/contractor-dashboard' },
  '/post-job': { label: 'Post Job', parent: '/my-jobs' },
  '/browse-jobs': { label: 'Browse Jobs', parent: null },
  '/job': { label: 'Job Details', parent: '/browse-jobs', isDynamic: true },
  '/saved-jobs': { label: 'Saved Jobs', parent: '/worker-dashboard' },
  '/my-applications': { label: 'My Applications', parent: '/worker-dashboard' },
  
  // Workers
  '/find-workers': { label: 'Find Workers', parent: null },
  '/worker': { label: 'Worker Profile', parent: '/find-workers', isDynamic: true },
  
  // Messages
  '/messages': { label: 'Messages', parent: null },
  
  // Escrow & Payments
  '/escrow': { label: 'Escrow', parent: '/contractor-dashboard' },
  '/escrow/': { label: 'Escrow Details', parent: '/escrow', isDynamic: true },
  
  // Project Planning
  '/project-planning': { label: 'Project Planning', parent: '/contractor-dashboard' },
  '/project-planning/': { label: 'Project Details', parent: '/project-planning', isDynamic: true },
  
  // Invoices
  '/invoices': { label: 'Invoices', parent: null },
  '/invoices/create': { label: 'Create Invoice', parent: '/invoices' },
  '/invoices/templates': { label: 'Templates', parent: '/invoices' },
  '/invoices/recurring': { label: 'Recurring', parent: '/invoices' },
  '/invoices/': { label: 'Invoice Details', parent: '/invoices', isDynamic: true },
  
  // Time Tracking
  '/time-clock': { label: 'Time Clock', parent: '/worker-dashboard' },
  '/timesheets': { label: 'Timesheets', parent: null },
  
  // Calendar & Reports
  '/calendar': { label: 'Calendar', parent: null },
  '/reports': { label: 'Reports', parent: null },
  
  // Team
  '/team': { label: 'Team', parent: '/contractor-dashboard' },
  
  // Admin
  '/admin': { label: 'Admin Dashboard', parent: null },
  
  // Pricing & Info
  '/pricing': { label: 'Pricing', parent: null },
  '/about': { label: 'About Us', parent: null },
  '/contact': { label: 'Contact', parent: null },
  '/privacy': { label: 'Privacy Policy', parent: null },
  '/terms': { label: 'Terms of Service', parent: null },
};

// Get the user type to determine the correct dashboard
const getUserDashboard = () => {
  const userType = localStorage.getItem('userType');
  return userType === 'contractor' ? '/contractor-dashboard' : '/worker-dashboard';
};

// Find route config for a path (handles dynamic routes)
const findRouteConfig = (pathname) => {
  // First try exact match
  if (routeConfig[pathname]) {
    return { config: routeConfig[pathname], path: pathname };
  }
  
  // Check for dynamic routes (e.g., /job/123, /worker/abc)
  const pathParts = pathname.split('/').filter(Boolean);
  if (pathParts.length >= 2) {
    const basePath = '/' + pathParts[0];
    const dynamicKey = basePath + '/';
    
    if (routeConfig[dynamicKey]) {
      return { config: routeConfig[dynamicKey], path: pathname };
    }
    
    // Also check without trailing slash
    if (routeConfig[basePath] && routeConfig[basePath].isDynamic) {
      return { config: routeConfig[basePath], path: pathname };
    }
  }
  
  return null;
};

// Build breadcrumb trail from current path
const buildBreadcrumbs = (pathname) => {
  const breadcrumbs = [];
  const routeInfo = findRouteConfig(pathname);
  
  if (!routeInfo) {
    // If route not found in config, just show Home > Current Page
    return [{ label: 'Home', path: getUserDashboard() }];
  }
  
  // Build trail by following parent chain
  let currentPath = pathname;
  let currentConfig = routeInfo.config;
  
  // Add current page
  breadcrumbs.unshift({
    label: currentConfig.label,
    path: currentPath,
    isCurrent: true
  });
  
  // Follow parent chain
  while (currentConfig.parent) {
    const parentPath = currentConfig.parent;
    const parentInfo = findRouteConfig(parentPath);
    
    if (parentInfo) {
      breadcrumbs.unshift({
        label: parentInfo.config.label,
        path: parentPath,
        isCurrent: false
      });
      currentConfig = parentInfo.config;
    } else {
      break;
    }
  }
  
  // Always add Home at the beginning if not already there
  if (breadcrumbs[0]?.path !== getUserDashboard() && breadcrumbs[0]?.path !== '/') {
    breadcrumbs.unshift({
      label: 'Home',
      path: getUserDashboard(),
      isCurrent: false,
      isHome: true
    });
  }
  
  return breadcrumbs;
};

const Breadcrumbs = ({ 
  className = '', 
  customItems = null,
  showHome = true,
  variant = 'default' // 'default', 'compact', 'pills'
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Skip breadcrumbs on certain pages
  const skipPages = ['/', '/login', '/signup', '/auth/callback'];
  if (skipPages.includes(pathname)) {
    return null;
  }
  
  // Use custom items if provided, otherwise build from path
  const breadcrumbs = customItems || buildBreadcrumbs(pathname);
  
  // Filter out home if not wanted
  const displayBreadcrumbs = showHome 
    ? breadcrumbs 
    : breadcrumbs.filter(b => !b.isHome);
  
  if (displayBreadcrumbs.length <= 1) {
    return null; // Don't show if only one item
  }
  
  // Variant styles
  const variantStyles = {
    default: {
      container: 'flex items-center gap-2 text-sm',
      item: 'text-neutral-500 dark:text-neutral-400 hover:text-[#2B4C6F] dark:hover:text-[#2B4C6F] transition-colors',
      current: 'text-neutral-900 dark:text-white font-semibold',
      separator: 'text-neutral-300 dark:text-neutral-600'
    },
    compact: {
      container: 'flex items-center gap-1.5 text-xs',
      item: 'text-neutral-500 dark:text-neutral-400 hover:text-[#2B4C6F] transition-colors',
      current: 'text-neutral-700 dark:text-neutral-200 font-medium',
      separator: 'text-neutral-300 dark:text-neutral-600'
    },
    pills: {
      container: 'flex items-center gap-2 text-sm',
      item: 'px-3 py-1 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 rounded-full hover:bg-[#2B4C6F] hover:text-white transition-all',
      current: 'px-3 py-1 bg-[#2B4C6F] text-white rounded-full font-medium',
      separator: 'text-neutral-300 dark:text-neutral-600'
    }
  };
  
  const styles = variantStyles[variant] || variantStyles.default;
  
  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`${styles.container} ${className}`}
      data-testid="breadcrumbs"
    >
      {displayBreadcrumbs.map((crumb, index) => (
        <React.Fragment key={crumb.path + index}>
          {index > 0 && (
            <CaretRight 
              size={14} 
              weight="bold" 
              className={styles.separator}
              aria-hidden="true"
            />
          )}
          
          {crumb.isCurrent ? (
            <span 
              className={styles.current}
              aria-current="page"
              data-testid={`breadcrumb-current`}
            >
              {crumb.isHome && <House size={16} weight="bold" className="inline mr-1" />}
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.path}
              className={`${styles.item} flex items-center`}
              data-testid={`breadcrumb-${crumb.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {crumb.isHome && <House size={16} weight="bold" className="mr-1" />}
              {crumb.isHome ? '' : crumb.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

// Tactical Brutalism styled breadcrumbs with box design
export const BreadcrumbsBox = ({ 
  className = '',
  customItems = null,
  title = null,
  showHome = true
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Skip breadcrumbs on certain pages
  const skipPages = ['/', '/login', '/signup', '/auth/callback'];
  if (skipPages.includes(pathname)) {
    return null;
  }
  
  const breadcrumbs = customItems || buildBreadcrumbs(pathname);
  const displayBreadcrumbs = showHome 
    ? breadcrumbs 
    : breadcrumbs.filter(b => !b.isHome);
  
  if (displayBreadcrumbs.length <= 1 && !title) {
    return null;
  }
  
  return (
    <div 
      className={`bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-lg shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F] px-4 py-2 ${className}`}
      data-testid="breadcrumbs-box"
    >
      <nav 
        aria-label="Breadcrumb" 
        className="flex items-center gap-2 text-sm flex-wrap"
      >
        {displayBreadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path + index}>
            {index > 0 && (
              <CaretRight 
                size={14} 
                weight="bold" 
                className="text-neutral-300 dark:text-neutral-600 flex-shrink-0"
                aria-hidden="true"
              />
            )}
            
            {crumb.isCurrent ? (
              <span 
                className="text-neutral-900 dark:text-white font-bold flex items-center"
                aria-current="page"
              >
                {crumb.isHome && <House size={16} weight="bold" className="mr-1.5" />}
                {title || crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-[#2B4C6F] hover:text-[#203A55] dark:text-[#7BAFD4] dark:hover:text-[#9CC5E8] font-medium flex items-center transition-colors"
              >
                {crumb.isHome && <House size={16} weight="bold" className="mr-1.5" />}
                {crumb.isHome ? 'Home' : crumb.label}
              </Link>
            )}
          </React.Fragment>
        ))}
      </nav>
    </div>
  );
};

// Simple inline breadcrumbs for headers
export const BreadcrumbsInline = ({ 
  items = [],
  className = ''
}) => {
  if (items.length === 0) return null;
  
  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`flex items-center gap-2 text-sm ${className}`}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <CaretRight 
              size={14} 
              weight="bold" 
              className="text-white/50"
              aria-hidden="true"
            />
          )}
          
          {item.href ? (
            <Link
              to={item.href}
              className="text-white/70 hover:text-white transition-colors"
            >
              {item.icon && <item.icon size={16} weight="bold" className="inline mr-1" />}
              {item.label}
            </Link>
          ) : (
            <span className="text-white font-semibold">
              {item.icon && <item.icon size={16} weight="bold" className="inline mr-1" />}
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumbs;
